class LocalStorageService {
  /**
   * @author Leonel Contreras
   * @description returns specific element from localstorage
   * @param {String} item
   * @returns {Object}
   * @memberof LocalStorageService
   */
  getLocalStorageItem = (item) => {
    let itemJSON;

    try {
      itemJSON = JSON.parse(localStorage.getItem(item));
    } catch (err) {
      itemJSON = null;
    }

    return itemJSON;
  };

  /**
   * @author Leonel Contreras
   * @description changes specific element from localstorage
   * @param {String} itemName
   * @param {Object} item
   * @returns {Boolean}
   * @memberof LocalStorageService
   */
  setLocalStorageItem = (itemName, item) => {
    localStorage.setItem(itemName, JSON.stringify(item));

    return true;
  };

  /**
   * @author Leonel Contreras
   * @description changes token and user in localStorage
   * @param {Object} token
   * @param {Object} user
   * @param {Array} modules
   * @returns {Boolean}
   * @memberof LocalStorageService
   */
  startLocalStorage = (token, user, modules) => {
    this.setLocalStorageItem("token", token);
    this.setLocalStorageItem("user", user);
    this.setLocalStorageItem("modules", modules);

    return true;
  };

  /**
   * @author Alan Aguilar
   * @description Set the start and finish time of user session with token in localStorage
   * @updatedDate 11-05-2021
   * @param {Object} token
   * @memberof LocalStorageService
   */
  timerSession = (token) => {
    let timeSessionStart = new Date();
    let msTokenExpiresIn = token.expiresIn * 1000; // Parse seconds to Miliseconds
    let msTimeSessionStart = timeSessionStart.getTime();
    let msTimeSessionEnd = msTimeSessionStart + msTokenExpiresIn;
    let timeSessionEnd = new Date(msTimeSessionEnd);
    let timeSession = { timeSessionStart, timeSessionEnd };
    this.setLocalStorageItem("timeSession", timeSession);
  };

  /**
   * @author Leonel Contreras
   * @description clear localStorage
   * @returns {Boolean}
   * @memberof LocalStorageService
   */
  clearLocalStorage = () => {
    localStorage.clear();

    return true;
  };

  deleteElementLocalStorage = (name) => {
    localStorage.removeItem(name);

    return true;
  };

  /**
   * @author Pedro Antonio
   * @description Get the user token
   * @static
   * @returns {string}
   * @memberof LocalStorageService
   */
  static retrieveToken = () => {
    let token;

    try {
      token = JSON.parse(localStorage.getItem("token")).idToken;
    } catch (err) {
      token = null;
    }

    return token;
  }

  /**
   * @author Pedro Antonio
   * @description Get the property
   * @param {string} name
   * @static
   * @memberof LocalStorageService
   */
  static retrieveItem = (name) => {
    let item;

    try {
      item = JSON.parse(localStorage.getItem(name));
    } catch (err) {
      item = null;
    }

    return item;
  }

  /**
   * @author Pedro Antonio
   * @description Get the property
   * @param {string[]} names
   * @static
   * @returns {object}
   * @memberof LocalStorageService
   */
  static retrieveItems = (names) => {
    let items = {};

    for (const name of names) {
      try {
        const item = JSON.parse(localStorage.getItem(name));
        if (item) items[name] = item
      } catch (err) {
        continue
      }
    }

    return items;
  }

  /**
   * @author Pedro Antonio
   * @description Store a item staticly
   * @date 2025-04-23
   * @static
   * @param {string} name
   * @param {any} item
   * @memberof LocalStorageService
   */
  static storeItem = (name, item) => {
    let savedSuccesfully = false
    try {
      localStorage.setItem(name, JSON.stringify(item))
      savedSuccesfully = true
      return savedSuccesfully
    } catch (e) {
      return savedSuccesfully
    }
  }

  /**
   * @author Pedro Antonio
   * @description Store multiple items staticly
   * @date 2025-04-23
   * @static
   * @param {{name: string, value: any}[]} items
   * @memberof LocalStorageService
   */
  static storeMultipleItems = (items) => {
    for (let index = items.length - 1; index >= 0; index--) {
      this.storeItem(items[index].name, JSON.stringify(items[index].name))
    }
  }
}

export { LocalStorageService as default };
